import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef } from 'react';
import { useParams } from 'next/navigation';
import { styled } from '@classify/react-core-components/styles';
import { translate } from '@openclassrooms/translations';
import Typography from '@classify/react-core-components/Typography';
import Box from '@classify/react-core-components/Box';
import PartTitle from './PartTitle';
import ChapterTocItem from './ChapterTocItem';
import QuizTocItem from './QuizTocItem';
import TocHeader from './TocHeader';
import { useCourseContext } from '../../_contexts/CourseContext';
export enum TocVariants {
  CHAPTER = 'CHAPTER',
  HOMEPAGE = 'HOMEPAGE',
  QUIZ = 'QUIZ',
}
const TocContainer = styled('div')<{
  variant: TocVariants;
}>(({
  variant
}) => ({
  height: '100%',
  overflow: variant === TocVariants.HOMEPAGE ? 'hidden' : 'scroll',
  overflowX: 'hidden'
}));
const PartsList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none'
});
const ChaptersList = styled('ul')<{
  isLastPart: boolean;
}>(({
  theme,
  isLastPart
}) => ({
  margin: theme.spacing(1, 0, 2, 0),
  padding: 0,
  '& > *': {
    position: 'relative',
    '&::before, &::after': {
      position: 'absolute',
      left: 19,
      height: 8,
      content: '" "',
      borderLeft: `1px solid ${theme.palette.line}`
    },
    '&::before': {
      top: 0,
      height: 4
    },
    '&::after': {
      top: 44,
      height: 'calc(100% - 40px)'
    },
    '&:first-of-type::before': {
      top: -8,
      height: 16
    },
    '&:last-child::after': {
      bottom: -16,
      height: 'calc(100% - 28px)'
    }
  },
  ...(isLastPart && {
    '& > *:last-child::after': {
      display: 'none'
    }
  })
}));
const TocTitle = styled(Typography)(({
  theme
}) => ({
  borderBottom: `1px solid ${theme.palette.line}`,
  paddingBottom: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(3)
  }
}));
export interface TableOfContentProps {
  color: string;
  onClose?: () => void;
  onClickItem?: () => void;
  variant?: TocVariants;
  sectionComponent?: any;
}
const getCurrentChapter = (flatToc, chapterId) => flatToc.find(tocElement => tocElement.id === chapterId);
const TableOfContent = t0 => {
  const $ = _c(30);
  const {
    color,
    sectionComponent,
    onClose: t1,
    onClickItem: t2,
    variant: t3
  } = t0;
  const onClose = t1 === undefined ? _temp : t1;
  const onClickItem = t2 === undefined ? _temp2 : t2;
  const variant = t3 === undefined ? TocVariants.CHAPTER : t3;
  const {
    course,
    tableOfContent,
    flatToc
  } = useCourseContext();
  const params = useParams();
  let t4;
  if ($[0] !== params) {
    t4 = params || {};
    $[0] = params;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    exerciseId,
    chapterId
  } = t4;
  const tocRef = useRef(null);
  let t5;
  if ($[2] !== tableOfContent) {
    t5 = tableOfContent || {
      children: []
    };
    $[2] = tableOfContent;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    children: parts
  } = t5;
  let t6;
  let t7;
  if ($[4] !== chapterId || $[5] !== exerciseId || $[6] !== flatToc || $[7] !== parts) {
    t6 = () => {
      if (!parts) {
        return;
      }
      const currentChapter = getCurrentChapter(flatToc, exerciseId ? Number(exerciseId) : chapterId);
      const currentChapterPart = parts.findIndex(part => part.id === currentChapter?.parentId) + 1;
      const selectedPart = document.querySelector(`#part-${currentChapterPart}`) as HTMLElement | null;
      if (tocRef?.current && selectedPart && currentChapterPart > 1) {
        tocRef.current.scrollTop = selectedPart.offsetTop - 75;
      }
    };
    t7 = [exerciseId, parts, chapterId, flatToc];
    $[4] = chapterId;
    $[5] = exerciseId;
    $[6] = flatToc;
    $[7] = parts;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[10] !== onClose || $[11] !== variant) {
    t8 = variant === TocVariants.CHAPTER && <TocHeader onClose={onClose} />;
    $[10] = onClose;
    $[11] = variant;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== variant) {
    t9 = variant === TocVariants.HOMEPAGE && <TocTitle variant="h3" component="h2">{translate("course_home.toc.title")}</TocTitle>;
    $[13] = variant;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== chapterId || $[16] !== color || $[17] !== course || $[18] !== onClickItem || $[19] !== parts || $[20] !== sectionComponent || $[21] !== variant) {
    t10 = parts?.map((part_0, n) => <li key={part_0.id}><PartTitle color={color} number={n + 1} component={sectionComponent} id={`${n + 1}`}>{part_0.title}</PartTitle><Box sx={{
        px: 2
      }}><ChaptersList isLastPart={n === Object.keys(parts).length - 1}>{part_0.children?.map((item, i) => item.type === "MCQ" ? <QuizTocItem key={`quiz-${item.id}`} quizPart={item} isOnHomePage={variant === "HOMEPAGE"} onClick={onClickItem} categoryColor={color} course={course} /> : <ChapterTocItem key={`chapter-${item.id}`} chapterPart={item} number={i + 1} isOnHomePage={variant === "HOMEPAGE"} onClick={onClickItem} categoryColor={color} currentChapterId={Number(chapterId)} course={course} />)}</ChaptersList></Box></li>);
    $[15] = chapterId;
    $[16] = color;
    $[17] = course;
    $[18] = onClickItem;
    $[19] = parts;
    $[20] = sectionComponent;
    $[21] = variant;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10) {
    t11 = <PartsList>{t10}</PartsList>;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t8 || $[27] !== t9 || $[28] !== variant) {
    t12 = <TocContainer ref={tocRef} variant={variant}>{t8}{t9}{t11}</TocContainer>;
    $[25] = t11;
    $[26] = t8;
    $[27] = t9;
    $[28] = variant;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
export default TableOfContent;
function _temp() {}
function _temp2() {}